import React from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControl,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";

const labelSx = {
    fontSize: 12,
    fontWeight: 700,
    color: "#5c6570",
    lineHeight: 1.3,
};

const hintSx = {
    fontSize: 11,
    color: "#94a3b8",
    lineHeight: 1.3,
};

const valueBoxSx = {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    px: 1.25,
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    color: "#1c2430",
    fontSize: 14,
    minWidth: 0,
    overflow: "hidden",
};

/** Label sits above the control so it never shares the input with the value. */
export function FieldShell({ label, hint, action, children, sx }) {
    return (
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5, ...sx }}>
            {label || action ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
                    {label ? (
                        <Typography component="span" sx={labelSx}>
                            {label}
                        </Typography>
                    ) : (
                        <span />
                    )}
                    {action}
                </Box>
            ) : null}
            {children}
            {hint ? (
                <Typography component="span" sx={hintSx}>
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}

/** Text or number stored on this record. The label is not inside the input. */
export function SkuTextField({ label, hint, sx, ...fieldProps }) {
    return (
        <FieldShell label={label} hint={hint} sx={sx}>
            <TextField
                {...fieldProps}
                size="small"
                fullWidth
                hiddenLabel
                placeholder=""
            />
        </FieldShell>
    );
}

/** Single-select stored on this record. Choose a choice, or clear it. */
export function SingleSelectField({
    label,
    value,
    onChange,
    choices = [],
    disabled = false,
    hint,
    onManage,
}) {
    const saved = choices.filter((choice) => choice !== "");
    const options = value && !saved.includes(value) ? [value, ...saved] : saved;
    const action = onManage ? (
        <Button
            size="small"
            onClick={onManage}
            sx={{
                textTransform: "none",
                fontWeight: 700,
                minWidth: 0,
                px: 0.5,
                py: 0,
                color: "#0d9488",
            }}
        >
            Edit options
        </Button>
    ) : null;
    return (
        <FieldShell label={label} hint={hint} action={action}>
            <FormControl size="small" fullWidth disabled={disabled}>
                <Select
                    displayEmpty
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    renderValue={(selected) => selected || "None"}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {options.map((choice) => (
                        <MenuItem key={choice} value={choice}>
                            {choice}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </FieldShell>
    );
}

/** Multiple choices stored on this record. Add or remove them here. */
export function MultiSelectField({
    label,
    value = [],
    onChange,
    choices = [],
    disabled = false,
    hint,
}) {
    const selected = Array.isArray(value) ? value : [];
    return (
        <FieldShell label={label} hint={hint}>
            <FormControl size="small" fullWidth disabled={disabled}>
                <Select
                    multiple
                    displayEmpty
                    value={selected}
                    onChange={(e) => onChange(e.target.value)}
                    renderValue={(vals) => (vals.length ? vals.join(", ") : "None")}
                >
                    {choices.filter(Boolean).map((choice) => (
                        <MenuItem key={choice} value={choice}>
                            <Checkbox checked={selected.includes(choice)} />
                            <ListItemText primary={choice} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </FieldShell>
    );
}

function recordLabel(option) {
    return option?.name || option?.code || String(option?._id || "");
}

/** One linked record from another table. Clear the field to remove the link. */
export function DimensionSingleSelect({
    label,
    valueId,
    options = [],
    onChange,
    disabled = false,
    hint,
}) {
    const selected = options.find((option) => String(option._id) === String(valueId)) || null;
    return (
        <FieldShell label={label} hint={hint || "Choose a record from this table"}>
            <Autocomplete
                size="small"
                disabled={disabled}
                options={options}
                value={selected}
                getOptionLabel={recordLabel}
                isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
                onChange={(_event, next) => onChange(next ? next._id : "")}
                renderInput={(params) => (
                    <TextField {...params} hiddenLabel placeholder="Search records" />
                )}
            />
        </FieldShell>
    );
}

/** Linked records from another table. Add or remove links on this SKU. */
export function DimensionMultiSelect({
    label,
    valueIds = [],
    options = [],
    onChange,
    disabled = false,
    hint,
}) {
    const selected = options.filter((option) =>
        valueIds.map(String).includes(String(option._id))
    );
    return (
        <FieldShell label={label} hint={hint || "Add or remove records from this table"}>
            <Autocomplete
                multiple
                size="small"
                disabled={disabled}
                options={options}
                value={selected}
                getOptionLabel={recordLabel}
                isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
                onChange={(_event, next) => onChange(next.map((option) => option._id))}
                renderInput={(params) => (
                    <TextField {...params} hiddenLabel placeholder="Search records" />
                )}
            />
        </FieldShell>
    );
}

/** Formula, rollup, or lookup. Shown as text, not an editable input. */
export function LookupField({ label, value, source }) {
    const empty = value == null || value === "";
    return (
        <FieldShell label={label} hint={source}>
            <Box sx={{ ...valueBoxSx, color: empty ? "#94a3b8" : "#1c2430" }}>
                <Box
                    component="span"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {empty ? "—" : String(value)}
                </Box>
            </Box>
        </FieldShell>
    );
}
